/**
 * Libro de estilo del Pórtico «Despacho» (GEN-PX-001).
 *
 * Página OCULTA: no está en el menú (el menú lo emite el kernel) y solo se
 * abre por la ruta `#/libro-de-estilo`, y únicamente para administradores. El
 * guard de rol aquí es de presentación (R-PT-04): la página no muestra nada
 * sensible, solo el estilo y la config pública del tenant.
 *
 * Todo sale de los tokens --dk-*: los swatches usan `var(--dk-…)` y el valor
 * mostrado es el color RESUELTO por el navegador, así que si el tenant cambia
 * una marca, la página lo refleja (incluidos los tonos derivados).
 */
import { useMemo } from 'react'
import { useSession } from '../context/SessionContext.jsx'
import tenant from '../tenant/tenant.config.js'
import { Boton, Tarjeta, Insignia, EtiquetaTipo, Avatar, Tooltip } from './ui.jsx'

export const RUTA_LIBRO_ESTILO = '#/libro-de-estilo'

const PALETA = [
  { grupo: 'Marca (del tenant)', items: [
    ['--dk-principal', 'Principal', 'Acción, marca, positivo'],
    ['--dk-profundo', 'Profundo', 'Raíl, cabecera (inicio)'],
    ['--dk-acento', 'Acento', 'Fin de degradado, halos'],
  ] },
  { grupo: 'Derivados de la marca', items: [
    ['--dk-tinta', 'Tinta', 'Raíl (final), tooltips'],
    ['--dk-medio', 'Medio', 'Tramo central del degradado'],
    ['--dk-claro', 'Claro', 'Fondos de énfasis suave'],
  ] },
  { grupo: 'Neutros y semánticos', items: [
    ['--dk-fondo', 'Fondo', 'Fondo de página'],
    ['--dk-panel', 'Panel', 'Tarjetas y paneles'],
    ['--dk-borde', 'Borde', 'Bordes y separadores'],
    ['--dk-texto', 'Texto', 'Texto principal'],
    ['--dk-texto-2', 'Texto 2', 'Texto secundario'],
    ['--dk-texto-3', 'Texto 3', 'Metadatos'],
    ['--dk-ambar', 'Ámbar', 'Decisión / te preguntan'],
    ['--dk-azul', 'Azul', 'Paso de proceso'],
    ['--dk-activo', 'Activo', 'Agente o persona activa'],
  ] },
]

const ESCALA = [
  ['Título de cabecera', 'cabecera', '26 px · 700'],
  ['Título de sección', 'seccion', '15 px · 700'],
  ['Título de tarjeta', 'tarjeta', '14 px · 600'],
  ['Cuerpo', 'cuerpo', '14 px · 400'],
]

const GRADIENTES = [
  ['--dk-grad-cabecera', 'Cabecera'],
  ['--dk-grad-rail', 'Raíl'],
  ['--dk-grad-agente', 'Agente / progreso'],
]

/** Color resuelto por el navegador (rgb/color(srgb)) → #RRGGBB; si no se puede, ''. */
export function aHex(css) {
  const rgb = css.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/)
  let c
  if (rgb) c = [rgb[1], rgb[2], rgb[3]].map(Number)
  else {
    const s = css.match(/^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/)
    if (!s) return ''
    c = [s[1], s[2], s[3]].map(v => Math.round(parseFloat(v) * 255))
  }
  return '#' + c.map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()
}

function colorResuelto(variable) {
  if (typeof document === 'undefined') return ''
  const sonda = document.createElement('span')
  sonda.style.color = `var(${variable})`
  document.body.appendChild(sonda)
  const valor = getComputedStyle(sonda).color
  sonda.remove()
  return aHex(valor)
}

function Seccion({ titulo, children }) {
  return (
    <section className="ls-seccion">
      <h2 className="ls-h2">{titulo}</h2>
      {children}
    </section>
  )
}

export default function LibroEstilo() {
  const { user } = useSession()
  const hex = useMemo(() => {
    const m = {}
    for (const g of PALETA) for (const [v] of g.items) m[v] = colorResuelto(v)
    return m
  }, [])

  if (user?.rol !== 'admin') {
    return <div className="ls ls-vacio dk" role="alert">Esta página es solo para administradores.</div>
  }

  return (
    <div className="ls dk" data-testid="libro-estilo">
      <header className="ls-cabecera">
        <span className="ls-eyebrow">Libro de estilo · Despacho</span>
        <h1 className="ls-h1">{tenant.nombre}</h1>
        <p>Colores, tipografía, espaciados y componentes del Pórtico. Todo desarrollo nuevo usa estos tokens.</p>
      </header>

      <Seccion titulo="Paleta">
        {PALETA.map(g => (
          <div key={g.grupo}>
            <p className="dk-meta ls-grupo">{g.grupo}</p>
            <div className="ls-rejilla">
              {g.items.map(([variable, nombre, uso]) => (
                <div key={variable} className="dk-tarjeta ls-swatch">
                  <span className="ls-muestra" style={{ background: `var(${variable})` }} />
                  <b className="dk-tarjeta__titulo">{nombre}</b>
                  <code>{hex[variable]}</code>
                  <span className="dk-meta">{uso}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="dk-meta ls-grupo">Degradados (solo estos tres)</p>
        <div className="ls-rejilla">
          {GRADIENTES.map(([variable, nombre]) => (
            <div key={variable} className="dk-tarjeta ls-swatch">
              <span className="ls-muestra" style={{ background: `var(${variable})` }} />
              <b className="dk-tarjeta__titulo">{nombre}</b>
            </div>
          ))}
        </div>
      </Seccion>

      <Seccion titulo="Tipografía">
        <Tarjeta>
          {ESCALA.map(([nombre, clave, medida]) => (
            <div key={clave} className="ls-fila-tipo">
              <span style={{ fontSize: `var(--dk-fs-${clave})`, fontWeight: `var(--dk-fw-${clave})` }}>{nombre}</span>
              <span className="dk-meta">{medida}</span>
            </div>
          ))}
          <div className="ls-fila-tipo">
            <span style={{ fontSize: 'var(--dk-fs-meta)' }}>Metadatos</span>
            <span className="dk-meta">12 px</span>
          </div>
          <div className="ls-fila-tipo">
            <EtiquetaTipo>Etiqueta de tipo</EtiquetaTipo>
            <span className="dk-meta">11 px · 700 · mayúsculas · 0,08 em</span>
          </div>
          <div className="ls-fila-tipo">
            <Insignia>12</Insignia>
            <span className="dk-meta">Micro (contadores, insignias) 10–11 px · 700</span>
          </div>
        </Tarjeta>
      </Seccion>

      <Seccion titulo="Componentes">
        <div className="ls-rejilla">
          <Tarjeta titulo="Botones">
            <div className="ls-fila">
              <Boton variante="primario">Primario</Boton>
              <Boton>Secundario</Boton>
            </div>
          </Tarjeta>
          <Tarjeta titulo="Tarjeta">
            <p className="dk-meta">Panel con borde, radio y sombra suave.</p>
          </Tarjeta>
          <Tarjeta titulo="Insignia">
            <div className="ls-fila"><Insignia>3</Insignia><Insignia>12</Insignia></div>
          </Tarjeta>
          <Tarjeta titulo="Etiqueta de tipo">
            <div className="ls-fila">
              <EtiquetaTipo>Aprobación</EtiquetaTipo>
              <EtiquetaTipo tipo="ambar">Decisión</EtiquetaTipo>
              <EtiquetaTipo tipo="azul">Paso de proceso</EtiquetaTipo>
            </div>
          </Tarjeta>
          <Tarjeta titulo="Avatar">
            <div className="ls-fila">
              <Avatar foto="/favicon.svg" nombre="Agente con foto" tamano={44} activo />
              <Avatar iniciales="JD" nombre="Persona con iniciales" tamano={44} activo />
              <Avatar iniciales="AN" nombre="Persona sin actividad" tamano={32} />
            </div>
          </Tarjeta>
          <Tarjeta titulo="Tooltip">
            <div className="ls-fila ls-fila--tooltip">
              <Tooltip texto="Nombre del asunto" detalle="Línea secundaria" abierto>
                <Boton>Pasa el ratón</Boton>
              </Tooltip>
            </div>
          </Tarjeta>
        </div>
      </Seccion>

      <Seccion titulo="Tenant activo">
        <p className="dk-meta">
          Por ahora la marca se cambia en src/tenant/tenant.config.js; editarla desde el Pórtico llegará en una historia posterior.
        </p>
        <Tarjeta>
          <dl className="ls-tenant">
            <dt>Nombre</dt><dd>{tenant.nombre}</dd>
            <dt>Logo</dt>
            <dd>{tenant.logo ? <><img className="ls-logo" src={tenant.logo} alt={`Logo de ${tenant.nombre}`} /> <code>{tenant.logo}</code></> : '—'}</dd>
            <dt>Fuente</dt>
            <dd><span style={{ fontFamily: 'var(--dk-font)' }}>{tenant.fuente.familia}</span>{tenant.fuente.url ? <> · <code>{tenant.fuente.url}</code></> : ' · auto-alojada'}</dd>
            <dt>Idioma</dt><dd>{tenant.idioma}</dd>
            <dt>Colores</dt>
            <dd className="ls-fila">
              {Object.entries(tenant.colores).map(([nombre, valor]) => (
                <span key={nombre} className="ls-chip-color">
                  <span className="ls-muestra ls-muestra--mini" style={{ background: `var(--dk-${nombre})` }} />
                  {nombre} <code>{valor}</code>
                </span>
              ))}
            </dd>
          </dl>
        </Tarjeta>
      </Seccion>
    </div>
  )
}
